import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
// Imported from the module, not the package barrel: the barrel re-exports this
// step, and going through it would close a cycle.
import { COLLECT_RESULTS_RESULTS_SOCKET_ID } from "../../utils/dynamicSockets/collectResultsInputSockets";

/**
 * Collect Results Step
 *
 * Pairs each item of a fanned-out loop with what happened to it, so a run can
 * say which items succeeded and which failed, and why.
 *
 * ```
 * Read ──[array per column]──▶ Loop step (Batch) ──▶ Await (all_settled) ──▶ Collect Results
 *   └──────────────── the same column lists ──────────────────────────────────▲
 * ```
 *
 * An Await in `all_settled` mode yields one outcome per item, by `fanoutIndex`,
 * but not the inputs that item ran with. Collect Results takes the outcomes on
 * `results` and the original per-item lists on its item inputs (one array
 * socket per configured key, wired from the same source as the loop), and joins
 * them by item position.
 *
 * An item fails when its step errored (`error`), when the configured failure
 * field of its result is `false` (`reported_failure`), or when the configured
 * output field does not contain the configured item input (`wrong_value` —
 * e.g. a downloaded file whose name lacks the requested account number). With
 * no rule configured, only errors fail.
 *
 * Failed items keep their original inputs, so a later run can be started with
 * exactly those items (the run page's "Re-run failed" action does).
 */

/** The outcome each failed item is labelled with. */
export const COLLECT_RESULTS_FAILURE_REASONS = [
  "error",
  "reported_failure",
  "wrong_value",
] as const;
export type CollectResultsFailureReason =
  (typeof COLLECT_RESULTS_FAILURE_REASONS)[number];

const itemInputKeySchema = z
  .string()
  .regex(
    /^[a-z][a-z0-9_]*$/,
    "Use lowercase letters, digits and underscores, starting with a letter",
  );

export const collectResultsConfigSchema = z
  .object({
    itemInputs: z
      .array(
        z.object({
          key: itemInputKeySchema.describe(
            "The item input's key — its socket id, and its name in each collected item's `inputs`",
          ),
          label: z.string().optional(),
        }),
      )
      .default([])
      .describe(
        "The original per-item lists the loop ran over (wire the same column lists that feed the looped step). Each becomes an array input; item i of every list belongs to the loop's item i.",
      ),
    failureField: z
      .string()
      .min(1)
      .optional()
      .describe(
        "A field of each item's result (dot path) whose `false` value means the item failed, e.g. `success`.",
      ),
    mustContain: z
      .object({
        outputField: z
          .string()
          .optional()
          .describe(
            "A field of each item's result (dot path, applied to every element when the result is a list), e.g. `fileName`. Empty: the result itself.",
          ),
        inputKey: itemInputKeySchema.describe(
          "The item input whose value must appear in that field, e.g. `account_number`.",
        ),
      })
      .optional()
      .describe(
        "An item fails unless its result's field contains its own input value — e.g. the downloaded file name contains the requested account number.",
      ),
  })
  .superRefine((config, ctx) => {
    const keys = config.itemInputs.map((input) => input.key);
    keys.forEach((key, index) => {
      if (key === COLLECT_RESULTS_RESULTS_SOCKET_ID) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `"${key}" is reserved for the Await's results`,
          path: ["itemInputs", index, "key"],
        });
      } else if (keys.indexOf(key) !== index) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Duplicate item input "${key}"`,
          path: ["itemInputs", index, "key"],
        });
      }
    });
    if (config.mustContain && !keys.includes(config.mustContain.inputKey)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `"${config.mustContain.inputKey}" is not one of the item inputs`,
        path: ["mustContain", "inputKey"],
      });
    }
  });

const succeededItemSchema = z.object({
  index: z.number().int(),
  inputs: z.record(z.string(), z.unknown()),
  value: z.unknown(),
});

const failedItemSchema = z.object({
  index: z.number().int(),
  inputs: z.record(z.string(), z.unknown()),
  reason: z.enum(COLLECT_RESULTS_FAILURE_REASONS),
  message: z.string(),
  /** For `wrong_value`: what the output field held instead. */
  found: z.array(z.string()).optional(),
});

export const collectResultsSummarySchema = z.object({
  total: z.number().int(),
  succeeded: z.number().int(),
  failed: z.number().int(),
  byReason: z.record(z.enum(COLLECT_RESULTS_FAILURE_REASONS), z.number().int()),
});

export const collectResultsOutputSchema = z.object({
  succeeded: z.array(succeededItemSchema),
  failed: z.array(failedItemSchema),
  summary: collectResultsSummarySchema,
});

export type CollectResultsSucceededItem = z.infer<typeof succeededItemSchema>;
export type CollectResultsFailedItem = z.infer<typeof failedItemSchema>;
export type CollectResultsSummary = z.infer<typeof collectResultsSummarySchema>;
export type CollectResultsOutput = z.infer<typeof collectResultsOutputSchema>;

export const collectResultsStep = defineStepConfig({
  type: "collect_results",
  name: "Collect Results",
  description:
    "Pairs each looped item's original inputs with its outcome from an Await (all settled), and splits the items into succeeded and failed with a reason",
  category: "Control Flow",

  // The item-input sockets are generated from `config.itemInputs`; passthrough
  // keeps them through validation.
  inputSchema: z
    .object({
      results: z
        .array(z.unknown())
        .describe(
          "The Await's results in all_settled mode: one outcome per looped item.",
        ),
    })
    .passthrough(),

  outputSchema: collectResultsOutputSchema,

  configSchema: collectResultsConfigSchema,

  emits: [] as const,
});
